import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import SiteHeader from "../../components/Header";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

const PAGE_SIZE = 10;

function PaymentNote({ note }) {
  const [show, setShow] = useState(false);

  if (!note) return null;

  return (
    <div 
      className="mt-1 position-relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ cursor: 'pointer' }}
    >
      <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }}>
        {note}
      </div>
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '0',
          backgroundColor: '#1e293b',
          color: '#fff',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 10,
          width: '200px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          whiteSpace: 'normal',
          marginBottom: '5px'
        }}>
          {note}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '20px',
            borderWidth: '5px',
            borderStyle: 'solid',
            borderColor: '#1e293b transparent transparent transparent'
          }}></div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Debounce search/fetch
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        // Use Internal API
        const params = new URLSearchParams({
          page: page,
          limit: PAGE_SIZE,
          search: search
        });

        const response = await fetch(`/api/clients?${params.toString()}`);
        
        if (!response.ok) {
           throw new Error('Failed to fetch clients');
        }

        const { clients: data, total } = await response.json();

        setClients(data);
        if (total !== null) setTotalClients(total);
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchClients();
    }, 300); // 300ms debounce for search

    return () => clearTimeout(debounceTimer);
  }, [page, search]);

  const totalPages = Math.ceil(totalClients / PAGE_SIZE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(p => p + 1);
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(p => p - 1);
  };

  return (
    <>
      <SEO />
      <SiteHeader />
      <main>
        <div className="page-wrapper">
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row g-2 align-items-center">
                <div className="col">
                  <h2 className="page-title mb-1">Dashboard</h2>
                  <div className="text-secondary">
                    Manage your clients and bookings
                  </div>
                </div>
                <div className="col-auto ms-auto d-print-none">
                  <div className="btn-list">
                    <button onClick={handleLogout} className="btn btn-outline-danger me-2">
                       Sign Out
                    </button>
                    <a href="/dashboard/create-client" className="btn btn-primary">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      <span className="d-none d-sm-inline">Create Client Entry</span>
                      <span className="d-sm-none">Create</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="page-body">
            <div className="container-xl">
              <div className="row row-cards">
                
                {/* Search & Stats */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-body border-bottom py-3">
                      <div className="d-flex">
                        <div className="text-secondary">
                          Show
                          <div className="mx-2 d-inline-block">
                            <input type="text" className="form-control form-control-sm" value={PAGE_SIZE} disabled size="3" aria-label="Entries count" />
                          </div>
                          entries
                        </div>
                        <div className="ms-auto text-secondary">
                          Search:
                          <div className="ms-2 d-inline-block">
                            <input 
                              type="text" 
                              className="form-control form-control-sm" 
                              aria-label="Search invoice" 
                              value={search}
                              onChange={handleSearchChange}
                              placeholder="Name, Email or Phone"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="table-responsive">
                      <table className="table card-table table-vcenter text-nowrap datatable">
                        <thead>
                          <tr>
                            <th className="w-1">ID</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Payment</th>
                            <th>Documents</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr><td colSpan="6" className="text-center py-4">Loading clients...</td></tr>
                          ) : clients.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-4">No clients found</td></tr>
                          ) : (
                            clients.map((client) => (
                              <tr key={client.id}>
                                <td><span className="text-secondary">{client.id}</span></td>
                                <td>
                                  <div className="font-weight-medium">{client.name}</div>
                                  {client.another_name && <div className="text-muted small">({client.another_name})</div>}
                                </td>
                                <td>
                                  <div>{client.email}</div>
                                  <div className="text-secondary">{client.phone}</div>
                                </td>
                                <td title={client.payment_note || ''}>
                                  <div className="font-weight-bold">₹{client.payment}</div>
                                  <div className="small text-secondary">{client.payment_method}</div>
                                  <div className="d-flex align-items-center mt-1">
                                    <span className={`badge ${client.payment_status === 'Completed' ? 'bg-success-lt' : 'bg-warning-lt'} me-2`}>
                                      {client.payment_status}
                                    </span>
                                  </div>
                                  <PaymentNote note={client.payment_note} />
                                </td>
                                <td>
                                  {/* Handle both old format (adhaar_front_url/adhaar_back_url) and new format (adhaar_images array) */}
                                  {client.adhaar_images && client.adhaar_images.length > 0 ? (
                                    <>
                                      {client.adhaar_images.map((url, idx) => (
                                        <a 
                                          key={idx} 
                                          href={url} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className="badge bg-primary me-1"
                                          title={`Image ${idx + 1}`}
                                        >
                                          {idx + 1}
                                        </a>
                                      ))}
                                    </>
                                  ) : (
                                    <>
                                      {client.adhaar_front_url && (
                                        <a 
                                          href={client.adhaar_front_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className="badge bg-primary me-1"
                                          title="Image 1"
                                        >
                                          1
                                        </a>
                                      )}
                                      {client.adhaar_back_url && (
                                        <a 
                                          href={client.adhaar_back_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className="badge bg-primary me-1"
                                          title="Image 2"
                                        >
                                          2
                                        </a>
                                      )}
                                    </>
                                  )}
                                </td>
                                <td>{new Date(client.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="card-footer d-flex align-items-center">
                      <p className="m-0 text-secondary">
                        Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to <span>{Math.min(page * PAGE_SIZE, totalClients)}</span> of <span>{totalClients}</span> entries
                      </p>
                      <ul className="pagination m-0 ms-auto">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={handlePrevPage} disabled={page === 1}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="15 6 9 12 15 18" /></svg>
                            prev
                          </button>
                        </li>
                        <li className="page-item active"><span className="page-link">{page}</span></li>
                        <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={handleNextPage} disabled={page >= totalPages}>
                            next
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18" /></svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
